"use client"
import { useChains } from "@opensea/app-kit"
import { useNumberFormatter } from "@opensea/i18n"
import {
  Flex,
  FlexCenter,
  FlexEnd,
  ItemAvatar,
  ItemContent,
  ItemTitle,
  Media,
  NumberDisplay,
  QuantityBadge,
  QuantityInput,
  SkeletonBlock,
  SkeletonLine,
  SpaceBetween,
  TABLE_SIZES,
  TableCell,
  TableRow,
  TextBody,
  TextOverflowTooltip,
  useTable,
} from "@opensea/ui-kit"
import { useOnChange } from "@opensea/ui-kit/hooks"
import { bn } from "@opensea/utils"
import type { FragmentOf } from "gql.tada"
import { readFragment } from "gql.tada"
import {
  Controller,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form"
import { useTranslations } from "use-intl"
import { InlineSwapButton } from "../../../../../components/InlineSwapButton"
import { Item } from "../../../../../components/Item"
import { OfferAmountInput } from "../../../../../components/OfferAmountInput"
import { useSyncFormFields } from "../../../../../hooks/useSyncFormField"
import { calculateOrderTotals } from "../../../../../lib/fees"
import { useBalances } from "../../../../../providers/BalancesProvider"
import {
  OFFER_MAX_DECIMALS,
  ORDER_PRICE_HIGH_SEVERITY_WARNING_THRESHOLD_PERCENTAGE,
  ORDER_PRICE_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE,
  TRAIT_OFFER_HIGH_SEVERITY_WARNING_THRESHOLD_PERCENTAGE,
  TRAIT_OFFER_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE,
} from "../../../constants"
import type { CreateCollectionOfferFormValues } from "../../types"
import { CreateCollectionOfferFlowTableRowFragment } from "./CreateCollectionOfferFlowTableRow.fragment"
import { CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES } from "./constants"

type Props = {
  item: FragmentOf<typeof CreateCollectionOfferFlowTableRowFragment> | undefined
  index: number
  context?: {
    isReview: boolean
  }
}

export const CreateCollectionOfferFlowTableRow = ({
  item: itemKey,
  index,
  context,
}: Props) => {
  const t = useTranslations("wallet.CreateCollectionOfferFlowTableRow")
  const collection = readFragment(
    CreateCollectionOfferFlowTableRowFragment,
    itemKey,
  )
  const { getNativeCurrency, getOfferCurrency } = useChains()

  const { control, trigger, setValue } =
    useFormContext<CreateCollectionOfferFormValues>()
  const { errors } = useFormState({ control })
  const offers = useWatch({ control, name: "offers" })
  const offer = offers[index]
  const format = useNumberFormatter()
  const isReview = context?.isReview ?? false
  const error = errors.offers?.[index]?.price
  const { withoutFees } = calculateOrderTotals(offer ? [offer] : [])
  const { getBalanceOfCurrencyBySymbol } = useBalances()

  const { size } = useTable()
  const imageSize = TABLE_SIZES[size].image
  const traitFloorPrice = offer?.traitFloorPrice
  // Use trait floor price as warning price
  const floorPrice =
    traitFloorPrice ?? collection?.floorPrice?.pricePerItem.token.unit
  const floorSymbol = collection?.floorPrice?.pricePerItem.token.symbol

  const lowSeverityWarningThreshold =
    (offer?.trait || (offer?.traits && offer.traits.length > 0)) &&
    !traitFloorPrice
      ? TRAIT_OFFER_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE
      : ORDER_PRICE_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE

  const highSeverityWarningThreshold =
    (offer?.trait || (offer?.traits && offer.traits.length > 0)) &&
    !traitFloorPrice
      ? TRAIT_OFFER_HIGH_SEVERITY_WARNING_THRESHOLD_PERCENTAGE
      : ORDER_PRICE_HIGH_SEVERITY_WARNING_THRESHOLD_PERCENTAGE

  const priceWarning =
    floorPrice &&
    offer?.price &&
    offer.price > 0 &&
    bn(offer.price).gt(
      bn(floorPrice)
        .times(1 + lowSeverityWarningThreshold)
        .decimalPlaces(OFFER_MAX_DECIMALS),
    )
      ? t("priceAboveFloor", {
          type: traitFloorPrice ? t("trait") : t("collection"),
          price: bn(floorPrice).decimalPlaces(OFFER_MAX_DECIMALS).toString(),
          symbol: floorSymbol ?? "",
        })
      : undefined
  const showHighSevPriceWarning =
    floorPrice !== undefined &&
    Boolean(priceWarning) &&
    offer?.price &&
    bn(offer.price).gt(
      bn(floorPrice)
        .times(1 + highSeverityWarningThreshold)
        .decimalPlaces(OFFER_MAX_DECIMALS),
    )

  useSyncFormFields({
    fields: [
      {
        name: `offers.${index}.shouldConfirmPrice`,
        value: Boolean(showHighSevPriceWarning),
      },
    ],
    setValue,
  })

  // Seems to be an issue where the total amount error doesn't get triggered when only the quantity changes
  useOnChange(offer?.quantity, trigger)

  if (!collection) {
    return <CreateCollectionOfferFlowTableRowSkeleton />
  }

  const offerSymbol = collection.chain.offerCurrency.symbol

  const balance = getBalanceOfCurrencyBySymbol({
    chainIdentifier: collection.chain.identifier,
    symbol: offerSymbol,
  })

  const tokenBalance = bn(offer?.tokenBalance ?? 0)
  const totalOfferAmount = (offer?.price ?? 0) * (offer?.quantity || 1)
  const hasInsufficientBalance = tokenBalance?.lt(totalOfferAmount)

  const nativeCurrency = getNativeCurrency(collection.chain.identifier)
  const offerCurrency = getOfferCurrency(collection.chain.identifier)

  const quantityError = errors.offers?.[index]?.quantity

  const quantityContent = isReview ? (
    <QuantityBadge className="ml-1">{offer?.quantity}</QuantityBadge>
  ) : (
    <Controller
      control={control}
      name={`offers.${index}.quantity`}
      render={({ field }) => (
        <QuantityInput
          className="ml-1"
          error={Boolean(quantityError)}
          min={0}
          onValueChange={field.onChange}
          size="sm"
          value={field.value}
        />
      )}
    />
  )

  const quantityErrorContent = quantityError && (
    <TextBody color="error-1" size="xs">
      {quantityError.message || t("notEnoughItems")}
    </TextBody>
  )

  const priceInfo =
    (priceWarning && !isReview) || error ? (
      <TextBody
        color={
          priceWarning && !showHighSevPriceWarning && !error
            ? "warning"
            : "error-1"
        }
        size="xs"
      >
        {error ? error.message : priceWarning}
      </TextBody>
    ) : isReview ? null : (
      <TextBody color="text-secondary" size="xs">
        {t("balance")}{" "}
        {format(balance?.quantity ?? 0, {
          display: "standard",
          suffix: offerSymbol,
        })}
      </TextBody>
    )

  const collectionLockup = (
    <Item variant="unstyled">
      <ItemAvatar
        className="rounded"
        size={imageSize}
        src={collection.imageUrl}
      />
      <ItemContent>
        <FlexCenter className="gap-2">
          <TextOverflowTooltip>
            <ItemTitle>{collection.name}</ItemTitle>
          </TextOverflowTooltip>

          <Media greaterThanOrEqual="md">{quantityContent}</Media>
        </FlexCenter>
      </ItemContent>
    </Item>
  )

  const priceContent = isReview ? (
    <TextOverflowTooltip>
      <TextBody>
        <NumberDisplay suffix={offerSymbol} value={offer?.price} />
      </TextBody>
    </TextOverflowTooltip>
  ) : (
    <Flex className="items-center gap-2">
      <OfferAmountInput
        autoFocus={index === 0}
        chain={collection.chain.identifier}
        offerIndex={index}
        warning={Boolean(priceWarning)}
      />
      {hasInsufficientBalance && (
        <InlineSwapButton
          initialFromCurrency={{
            symbol: nativeCurrency.symbol,
            contractAddress: nativeCurrency.contractAddress,
            chainIdentifier: collection.chain.identifier,
          }}
          initialToCurrency={{
            symbol: offerCurrency.symbol,
            contractAddress: offerCurrency.contractAddress,
            chainIdentifier: collection.chain.identifier,
          }}
        />
      )}
    </Flex>
  )

  return (
    <>
      <Media lessThan="md">
        <TableRow>
          <TableCell
            className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.collection}
          >
            {collectionLockup}
          </TableCell>

          <TableCell
            className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.price}
          >
            {quantityContent}
          </TableCell>
        </TableRow>

        {quantityErrorContent}

        <TableRow>
          <TableCell
            className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.collection}
          >
            <TextBody weight="semibold">{t("offerPrice")}</TextBody>
          </TableCell>

          <TableCell
            className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.price}
          >
            {priceContent}
          </TableCell>
        </TableRow>

        <FlexEnd>{priceInfo}</FlexEnd>
      </Media>
      <Media greaterThanOrEqual="md">
        <TableRow>
          <TableCell
            className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.collection}
          >
            {collectionLockup}
          </TableCell>
          <TableCell
            className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.floor}
          >
            <TextOverflowTooltip>
              <TextBody>
                <NumberDisplay
                  suffix={collection.floorPrice?.pricePerItem.token.symbol}
                  value={collection.floorPrice?.pricePerItem.token.unit}
                />
              </TextBody>
            </TextOverflowTooltip>
          </TableCell>
          {(offer?.trait || (offer?.traits && offer.traits.length > 0)) && (
            <TableCell
              className={
                CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.traitFloor
              }
            >
              <TextOverflowTooltip>
                <TextBody>
                  <NumberDisplay
                    suffix={collection.chain.listingCurrency.symbol}
                    value={offer?.traitFloorPrice}
                  />
                </TextBody>
              </TextOverflowTooltip>
            </TableCell>
          )}

          <TableCell
            className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.bestOffer}
          >
            <TextOverflowTooltip>
              <TextBody>
                <NumberDisplay
                  suffix={collection.topOffer?.pricePerItem.token.symbol}
                  value={collection.topOffer?.pricePerItem.token.unit}
                />
              </TextBody>
            </TextOverflowTooltip>
          </TableCell>

          {(offer?.trait || (offer?.traits && offer.traits.length > 0)) && (
            <TableCell
              className={
                CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.traitBestOffer
              }
            >
              <TextOverflowTooltip>
                <TextBody>
                  <NumberDisplay
                    suffix={collection.chain.offerCurrency.symbol}
                    value={offer?.traitTopOfferPrice}
                  />
                </TextBody>
              </TextOverflowTooltip>
            </TableCell>
          )}

          <TableCell
            className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.proceeds}
          >
            <TextOverflowTooltip>
              <TextBody>
                <NumberDisplay suffix={offerSymbol} value={withoutFees} />
              </TextBody>
            </TextOverflowTooltip>
          </TableCell>
          <TableCell
            className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.price}
          >
            {priceContent}
          </TableCell>
        </TableRow>
        {(Boolean(priceWarning) || error || quantityError) && (
          <SpaceBetween className="w-full">
            <div>{quantityErrorContent}</div>
            <div>{priceInfo}</div>
          </SpaceBetween>
        )}
      </Media>
    </>
  )
}

export const CreateCollectionOfferFlowTableRowSkeleton = () => {
  const { size } = useTable()
  const imageSize = TABLE_SIZES[size].image

  return (
    <TableRow>
      <TableCell
        className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.collection}
      >
        <Item variant="unstyled">
          <ItemAvatar className="rounded" size={imageSize}>
            <SkeletonBlock />
          </ItemAvatar>
          <ItemContent>
            <Flex className="gap-2">
              <SkeletonLine className="h-4 w-16" />
            </Flex>
          </ItemContent>
        </Item>
      </TableCell>
      <TableCell
        className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.floor}
      >
        <SkeletonLine className="h-4 w-20" />
      </TableCell>
      <TableCell
        className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.bestOffer}
      >
        <SkeletonLine className="h-4 w-20" />
      </TableCell>
      <TableCell
        className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.proceeds}
      >
        <SkeletonLine className="h-4 w-20" />
      </TableCell>
      <TableCell
        className={CREATE_COLLECTION_OFFER_FLOW_TABLE_CLASSNAMES.price}
      >
        <SkeletonLine className="h-8 w-full" />
      </TableCell>
    </TableRow>
  )
}
